import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Camera,
  Mic,
  Square,
  Clock,
  Package,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  CameraOff,
} from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Spinner } from '@/shared/ui/Spinner';
import { PhotoPolicyBadge } from '@/shared/ui/PhotoPolicyBadge';
import { ApiException } from '@/shared/api/client';
import {
  useCaptureContext,
  useCaptureEmployeeLookup,
  EMPLOYEE_LOOKUP_MIN,
  useSubmitReport,
  uploadMedia,
  type MediaPhase,
} from './capture.api';
import { useAudioRecorder } from './useAudioRecorder';
import { usePwaUpdate } from '@/shared/pwa/usePwaUpdate';

interface PendingPhoto {
  file: File;
  phase: MediaPhase;
  preview: string;
  progress: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
}

export function CapturePage() {
  const { token = '' } = useParams();
  const { data: ctx, isLoading, error } = useCaptureContext(token);
  const [nameSuche, setNameSuche] = useState('');
  const { data: treffer, isFetching: sucheLaeuft } = useCaptureEmployeeLookup(token, nameSuche);
  const submit = useSubmitReport(token);
  const audio = useAudioRecorder();

  // Vorgabe bleibt "Ohne Namen": leere employeeId. Die Namensangabe ist
  // freiwillig und darf durch das Nachschlagen nicht faktisch erzwungen
  // werden - sonst entstuende mehr unbelegte Zuordnung statt weniger.
  const [employeeId, setEmployeeId] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [noteText, setNoteText] = useState('');
  const [minutes, setMinutes] = useState('');
  const [materials, setMaterials] = useState<{ name: string; quantity: string; unit: string }[]>([]);
  const [extraTitle, setExtraTitle] = useState('');
  const [extraReason, setExtraReason] = useState('');
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [phase, setPhase] = useState<MediaPhase>('BEFORE');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  /**
   * Update-Behandlung auf der Erfassungsseite (ADR-033). Drei Zustände, und
   * entschieden wird nach dem, was auf dem Spiel steht - NICHT durch eine
   * Frage an jemanden, der im Treppenhaus mit Handschuhen tippt:
   *
   *  1. Unberührt  -> sofort anwenden. Es ist nichts zu verlieren, und genau
   *     dieser Fall ist am 15.09.2026 schiefgegangen: frisch geöffneter Link,
   *     alte App. Ein Hinweis wäre hier eine Hürde ohne Nutzen.
   *  2. In Arbeit  -> NICHTS tun und NICHTS einblenden. Keine Leiste, kein
   *     Hinweis, keine Layoutverschiebung; der Bildschirm bleibt, wie er ist.
   *     Ein stilles Update hier verlöre nicht abgesendete Fotos, eine laufende
   *     Aufnahme und eingetippte Zeiten (ADR-021).
   *  3. Abgesendet -> anwenden, sobald die Bestätigung steht.
   */
  const { updateVerfuegbar, anwenden } = usePwaUpdate();
  const inArbeit =
    photos.length > 0 ||
    noteText.trim() !== '' ||
    minutes !== '' ||
    materials.length > 0 ||
    extraTitle.trim() !== '' ||
    extraReason.trim() !== '' ||
    employeeId !== '' ||
    nameSuche.trim() !== '' ||
    audio.state === 'recording' ||
    audio.blob !== null ||
    submit.isPending;

  useEffect(() => {
    if (!updateVerfuegbar) return;
    if (result !== null || !inArbeit) anwenden();
  }, [updateVerfuegbar, result, inArbeit, anwenden]);
  const fileInput = useRef<HTMLInputElement>(null);

  if (isLoading) return <Spinner />;
  if (error || !ctx) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm rounded-xl bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-3 text-lg font-semibold">Link nicht gültig</h1>
          <p className="mt-1 text-sm text-slate-600">
            {error instanceof ApiException ? error.message : 'Bitte im Büro einen neuen Link anfordern.'}
          </p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
        <div className="max-w-sm rounded-xl bg-white p-6 text-center shadow-sm">
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
          <h1 className="mt-3 text-lg font-semibold">Meldung gesendet</h1>
          <p className="mt-1 text-sm text-slate-600">{result}</p>
          <Button size="lg" className="mt-5 w-full" onClick={() => window.location.reload()}>
            Weitere Meldung erfassen
          </Button>
        </div>
      </div>
    );
  }

  const photosForbidden = ctx.object.photoPolicy === 'FORBIDDEN';

  const onPickPhotos = (files: FileList | null) => {
    if (!files) return;
    const next = Array.from(files).map((file) => ({
      file,
      phase,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: 'pending' as const,
    }));
    setPhotos((p) => [...p, ...next]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const addMaterial = () => setMaterials((m) => [...m, { name: '', quantity: '', unit: 'Stück' }]);
  const setMat = (i: number, k: 'name' | 'quantity' | 'unit', v: string) =>
    setMaterials((m) => m.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));

  const canSubmit =
    noteText.trim() || audio.blob || minutes || photos.length > 0 || extraTitle.trim();

  const onSubmit = async () => {
    setSubmitError(null);
    try {
      // 1. Meldung anlegen (Text/Zeit/Material/Zusatz in EINER Transaktion)
      const mats = materials
        .filter((m) => m.name.trim() && Number(m.quantity) > 0)
        .map((m) => ({ name: m.name.trim(), quantity: Number(m.quantity), unit: m.unit }));
      const res = await submit.mutateAsync({
        ...(employeeId ? { employeeId } : {}),
        ...(noteText.trim() ? { noteText: noteText.trim() } : {}),
        ...(minutes ? { workTimes: [{ durationMinutes: Number(minutes) }] } : {}),
        ...(mats.length ? { materials: mats } : {}),
        ...(extraTitle.trim()
          ? {
              extraServices: [
                {
                  title: extraTitle.trim(),
                  ...(extraReason.trim() ? { reason: extraReason.trim() } : {}),
                },
              ],
            }
          : {}),
      });

      // 2. Medien nacheinander hochladen (Meldung existiert jetzt)
      for (let i = 0; i < photos.length; i++) {
        setPhotos((p) => p.map((x, idx) => (idx === i ? { ...x, status: 'uploading' } : x)));
        try {
          await uploadMedia(token, res.id, photos[i].file, 'PHOTO', photos[i].phase, (pct) =>
            setPhotos((p) => p.map((x, idx) => (idx === i ? { ...x, progress: pct } : x))),
          );
          setPhotos((p) => p.map((x, idx) => (idx === i ? { ...x, status: 'done' } : x)));
        } catch {
          setPhotos((p) => p.map((x, idx) => (idx === i ? { ...x, status: 'error' } : x)));
        }
      }
      if (audio.blob) {
        await uploadMedia(token, res.id, audio.blob, 'AUDIO', 'OTHER');
      }

      setResult(res.message);
    } catch (e) {
      setSubmitError(e instanceof ApiException ? e.message : 'Senden fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-28">
      {/* Kontext-Kopf */}
      <header className="border-b border-slate-200 bg-white px-4 py-3">
        <h1 className="font-semibold text-slate-900">{ctx.title}</h1>
        <p className="text-sm text-slate-600">
          {ctx.customerName} · {ctx.object.name}
        </p>
        <div className="mt-1">
          <PhotoPolicyBadge policy={ctx.object.photoPolicy} />
        </div>
        {ctx.object.accessNotes && (
          <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
            {ctx.object.accessNotes}
          </p>
        )}
      </header>

      <main className="space-y-4 p-4">
        {/* Mitarbeiter: Nachschlagen statt Vollauskunft (ADR-032).
            "Ohne Namen" ist die Vorgabe und bleibt ohne jede Eingabe gueltig. */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="wer-meldet">
            Wer meldet? <span className="font-normal text-slate-500">(freiwillig)</span>
          </label>

          {employeeId ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-300 px-3 py-2">
              <span className="text-sm text-slate-800">{employeeName}</span>
              <button
                type="button"
                className="text-sm text-slate-500 underline"
                onClick={() => {
                  setEmployeeId('');
                  setEmployeeName('');
                  setNameSuche('');
                }}
              >
                Ohne Namen melden
              </button>
            </div>
          ) : (
            <>
              <input
                id="wer-meldet"
                type="text"
                inputMode="text"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
                placeholder={`Name eingeben (ab ${EMPLOYEE_LOOKUP_MIN} Zeichen)`}
                value={nameSuche}
                onChange={(e) => setNameSuche(e.target.value)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Ohne Eingabe wird die Meldung ohne Namen erfasst.
              </p>

              {nameSuche.trim().length >= EMPLOYEE_LOOKUP_MIN && (
                <div className="mt-2 space-y-1">
                  {sucheLaeuft && <p className="text-xs text-slate-500">Suche laeuft …</p>}
                  {!sucheLaeuft && treffer && treffer.items.length === 0 && (
                    <p className="text-xs text-slate-500">Kein Treffer. Andere Schreibweise versuchen.</p>
                  )}
                  {treffer?.items.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      className="block w-full rounded-lg border border-slate-200 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => {
                        setEmployeeId(e.id);
                        setEmployeeName(e.displayName);
                      }}
                    >
                      {e.displayName}
                    </button>
                  ))}
                  {/* Gekappte Liste wird ANGESAGT - eine still gekuerzte Auswahl
                      sieht aus wie eine vollstaendige. */}
                  {treffer?.truncated && (
                    <p className="text-xs text-amber-700">
                      Es gibt weitere Treffer. Bitte mehr Zeichen eingeben.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* Fotos */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-medium text-slate-900">
            {photosForbidden ? <CameraOff className="h-5 w-5" /> : <Camera className="h-5 w-5" />} Fotos
          </h2>
          {photosForbidden ? (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              Für dieses Objekt sind keine Fotos erlaubt. Bitte per Sprache oder Text dokumentieren.
            </p>
          ) : (
            <>
              <div className="mb-3 flex gap-2">
                {(['BEFORE', 'AFTER', 'OTHER'] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPhase(p)}
                    className={`min-h-touch flex-1 rounded-lg border text-sm font-medium ${
                      phase === p
                        ? 'border-brand-700 bg-brand-700 text-white'
                        : 'border-slate-300 bg-white text-slate-700'
                    }`}
                  >
                    {p === 'BEFORE' ? 'Vorher' : p === 'AFTER' ? 'Nachher' : 'Sonstiges'}
                  </button>
                ))}
              </div>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={(e) => onPickPhotos(e.target.files)}
              />
              <Button size="lg" variant="secondary" className="w-full" onClick={() => fileInput.current?.click()}>
                <Camera className="h-5 w-5" /> Foto aufnehmen
              </Button>
              {photos.length > 0 && (
                <ul className="mt-3 grid grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <li key={i} className="relative">
                      <img src={p.preview} alt="" className="h-24 w-full rounded-lg object-cover" />
                      <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
                        {p.phase === 'BEFORE' ? 'Vorher' : p.phase === 'AFTER' ? 'Nachher' : 'Sonst.'}
                      </span>
                      {p.status === 'uploading' && (
                        <div className="absolute inset-x-1 bottom-1 h-1.5 overflow-hidden rounded bg-white/60">
                          <div className="h-full bg-brand-600" style={{ width: `${p.progress}%` }} />
                        </div>
                      )}
                      {p.status === 'error' && (
                        <span className="absolute inset-x-1 bottom-1 rounded bg-red-600 px-1 text-center text-[10px] text-white">
                          Fehler
                        </span>
                      )}
                      {p.status === 'pending' && (
                        <button
                          onClick={() => setPhotos((ph) => ph.filter((_, idx) => idx !== i))}
                          className="absolute right-1 top-1 rounded bg-black/60 p-1 text-white"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        {/* Sprache */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-medium text-slate-900">
            <Mic className="h-5 w-5" /> Kurz einsprechen
          </h2>
          {audio.state === 'unsupported' && (
            <p className="text-sm text-slate-500">Aufnahme wird von diesem Gerät nicht unterstützt.</p>
          )}
          {audio.state === 'denied' && (
            <p className="text-sm text-red-600">Mikrofon-Zugriff wurde abgelehnt.</p>
          )}
          {audio.state === 'idle' && (
            <Button size="lg" variant="secondary" className="w-full" onClick={audio.start}>
              <Mic className="h-5 w-5" /> Aufnahme starten
            </Button>
          )}
          {audio.state === 'recording' && (
            <Button size="lg" variant="danger" className="w-full" onClick={audio.stop}>
              <Square className="h-5 w-5" /> Stopp ({audio.seconds}s)
            </Button>
          )}
          {audio.state === 'done' && audio.blob && (
            <div className="flex items-center justify-between rounded-lg bg-green-50 px-3 py-2">
              <span className="text-sm text-green-700">
                Aufnahme bereit ({Math.round(audio.blob.size / 1024)} KB)
              </span>
              <button onClick={audio.reset} className="text-sm text-slate-500 underline">
                Verwerfen
              </button>
            </div>
          )}
        </section>

        {/* Text */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <label className="mb-2 block font-medium text-slate-900">Oder kurz tippen</label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            rows={3}
            placeholder="Was wurde gemacht?"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </section>

        {/* Zeit */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-medium text-slate-900">
            <Clock className="h-5 w-5" /> Arbeitszeit
          </h2>
          <div className="flex gap-2">
            {[15, 30, 45, 60, 90, 120].map((m) => (
              <button
                key={m}
                onClick={() => setMinutes(String(m))}
                className={`min-h-touch flex-1 rounded-lg border text-sm font-medium ${
                  minutes === String(m)
                    ? 'border-brand-700 bg-brand-700 text-white'
                    : 'border-slate-300 bg-white text-slate-700'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="Oder Minuten eingeben"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className="mt-2"
          />
        </section>

        {/* Material */}
        <section className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-medium text-slate-900">
            <Package className="h-5 w-5" /> Material <span className="text-sm font-normal text-slate-400">(optional)</span>
          </h2>
          {materials.map((m, i) => (
            <div key={i} className="mb-2 grid grid-cols-[1fr_5rem_6rem] gap-2">
              <Input placeholder="Bezeichnung" value={m.name} onChange={(e) => setMat(i, 'name', e.target.value)} />
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Menge"
                value={m.quantity}
                onChange={(e) => setMat(i, 'quantity', e.target.value)}
              />
              <Select value={m.unit} onChange={(e) => setMat(i, 'unit', e.target.value)}>
                {['Stück', 'l', 'kg', 'm', 'Paar', 'Packung'].map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </Select>
            </div>
          ))}
          <Button size="md" variant="ghost" onClick={addMaterial}>
            + Material hinzufügen
          </Button>
        </section>

        {/* Zusatzleistung */}
        <section className="rounded-xl border-2 border-amber-200 bg-white p-4 shadow-sm">
          <h2 className="mb-1 flex items-center gap-2 font-medium text-slate-900">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Zusatzleistung
          </h2>
          <p className="mb-3 text-xs text-slate-500">
            Etwas gemacht, das nicht zum Auftrag gehört? Hier melden – das Büro prüft und rechnet ab.
          </p>
          <Input
            placeholder="Was war zusätzlich? (z. B. Farbreste entfernt)"
            value={extraTitle}
            onChange={(e) => setExtraTitle(e.target.value)}
          />
          {extraTitle.trim() && (
            <Input
              placeholder="Warum war das nötig?"
              value={extraReason}
              onChange={(e) => setExtraReason(e.target.value)}
              className="mt-2"
            />
          )}
        </section>

        {submitError && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</div>
        )}
      </main>

      {/* Fixe Sendeleiste */}
      <div className="fixed inset-x-0 bottom-0 border-t border-slate-200 bg-white p-4">
        <Button
          size="lg"
          className="w-full"
          disabled={!canSubmit}
          loading={submit.isPending}
          onClick={onSubmit}
        >
          Meldung absenden
        </Button>
      </div>
    </div>
  );
}
